// `…/add-to-job` — the ATS-job panel, in a side view.
//
// ⚠️ THE SELECTION COMES FROM TWO DIFFERENT PLACES DEPENDING ON THE ROUTE, and that is a real
// difference rather than plumbing. Under the review screen the panel reads
// `entityCheckboxContainer.checkedEntities`, the shared list selection. Under the candidate
// profile the state SEEDS that container with exactly one entry — the candidate the profile is
// showing — in its own `init()`. So this route takes the candidate as a prop when it has one
// and falls back to the (unported) shared selection otherwise, which today is empty.
import { useEffect, useState, type ReactElement } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  AddToAtsJobSource,
  fetchViewModel,
  getAddToJobDefaultValues,
  getAllJobStatusItems,
  addToAtsJobAsync,
  outwardWriteOutcome,
  searchAtsJobsByTitle,
  type AtsJobStatusItem,
  type AddToAtsJobDefaults,
} from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import { AddToJobPanel, type AddToJobAtsOptions, getAtsLabel } from '@woo-ext/ui'

export interface AddToJobRouteProps {
  /** Where the ✕ goes back to, with the ids the state's `closeParams` carries. */
  closePath?: string
}

/** What the candidate profile hands its side view; null under the review screen. */
interface SideViewContext {
  checked?: ReadonlyArray<{ id: number; name?: string | null }>
}

export function AddToJobRoute({ closePath }: AddToJobRouteProps): ReactElement {
  const context = useOutletContext<SideViewContext | null>()
  const checked = context?.checked ?? []
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [atsOptions, setAtsOptions] = useState<AddToJobAtsOptions>({})
  const [statuses, setStatuses] = useState<AtsJobStatusItem[] | undefined>(undefined)
  const [defaults, setDefaults] = useState<AddToAtsJobDefaults>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const rawAlertId = params.get('alertId')
  const parsedAlertId = rawAlertId === null ? Number.NaN : Number.parseInt(rawAlertId, 10)
  const alertId = Number.isNaN(parsedAlertId) ? undefined : parsedAlertId

  const close = (): void => {
    if (closePath === undefined) return
    const candidateId = params.get('candidateId')
    const search = new URLSearchParams()
    if (alertId !== undefined) search.set('alertId', String(alertId))
    if (candidateId !== null) search.set('candidateId', candidateId)
    void navigate(search.size === 0 ? closePath : `${closePath}?${search.toString()}`)
  }

  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        const atsLabel = getAtsLabel(model)
        setAtsOptions({
          atsName: model.atsName ?? undefined,
          ...(atsLabel === undefined ? {} : { atsLabel }),
          atsHasJobCode: model.atsHasJobCode === true,
          atsHasConcreteEmployers: model.atsHasConcreteEmployers === true,
        })
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    let live = true
    // ⚠️ THE FORM WAITS FOR **BOTH**, and shows a spinner until then — upstream runs them
    // through one `$q.all` and only then picks the selected status out of the status list by
    // matching the default's `status` string. Rendering on the first response would open the
    // dropdown on "Select" and then jump.
    Promise.all([
      getAllJobStatusItems(),
      // `source: DEDICATED` — this panel, as opposed to the one inside the message composer.
      getAddToJobDefaultValues({ source: AddToAtsJobSource.Dedicated, ...(alertId === undefined ? {} : { alertId }) }),
    ]).then(
      ([items, values]) => {
        if (!live) return
        setDefaults(values)
        setStatuses(items)
      },
      () => {
        // The spinner stays, which is what upstream does: `this.form` is only set by
        // `onFormInit`, and `$onInit` awaits the two calls before reaching it.
        return undefined
      },
    )
    return () => {
      live = false
    }
  }, [alertId])

  return (
    <AddToJobPanel
      atsOptions={atsOptions}
      checked={checked}
      {...(statuses === undefined ? {} : { statuses })}
      {...(defaults.job === undefined || defaults.job === null ? {} : { defaultJob: defaults.job })}
      {...(defaults.status === undefined || defaults.status === null ? {} : { defaultStatus: defaults.status })}
      flexBasis={ExtensionViewWidth.Extended}
      onSearchJobs={async (query) => searchAtsJobsByTitle(query)}
      onClose={close}
      {...(error === undefined ? {} : { error })}
      submitting={submitting}
      /**
       * ⚠️ THIS WRITES INTO THE CUSTOMER'S ATS. `addToAtsJobAsync` enqueues a real record
       * against a real job. It fires only from this button.
       *
       * ⚠️ AND A NETWORK FAILURE REPORTS SUCCESS — see `outwardWriteOutcome`. The write is
       * deduped and idempotent server-side, so a request that never got an answer may well
       * have landed; saying it failed invites a second press and a duplicate submittal.
       */
      onSubmit={({ job, status }) => {
        if (submitting) return
        setSubmitting(true)
        setError(undefined)
        const candidateIds = checked.map((candidate) => candidate.id)
        addToAtsJobAsync({
          jobId: job.id,
          status,
          candidateIds,
          ...(alertId === undefined ? {} : { alertId }),
        }).then(
          () => {
            setSubmitting(false)
            close()
          },
          (failure: unknown) => {
            setSubmitting(false)
            const outcome = outwardWriteOutcome(failure)
            // `silent` is a 403: the auth layer owns it and is about to redirect.
            if (outcome.report === 'error') setError(outcome.message)
            else close()
          },
        )
      }}
    />
  )
}
